/**
 * Painéis do Grafana embutidos no CCO (US-408) — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 26/10/2026 · Versão: 1.2.0
 *
 * Login único (GUS-108): a tela pede ao backend um token de painel curto (`POST /api/v1/auth/token-painel`, audiência
 * `nexora-grafana`); o backend acerta o usuário nas organizações do Grafana com o papel do perfil. O token vai na URL
 * do painel (`auth_token`) e o próprio Grafana o repassa nas consultas: sem senha do Grafana, sem cookie. O Grafana é
 * servido em `/paineis/`, na mesma origem da tela (proxy do Vite ou do Caddy): o navegador não precisa confiar na CA
 * interna. O token é renovado 2 min antes de vencer (o painel recarrega). Os painéis só leem o hub (ADR-0006 v2.0).
 * v1.1 (GUS-1509): `PainelExpansivel` — o painel pequeno funciona como miniatura; o clique abre em tela cheia, com
 * "Voltar" e Esc (o iframe engole o clique, por isso há uma camada transparente por cima na miniatura).
 */
import { useEffect, useRef, useState } from "react";
import { api, ErroApi } from "../api";

type TokenPainel = { token: string; expira_em: string; grafana_url: string; organizacoes: { empresa: string; org_id: number; papel: string }[] };
type Estado = { dados: TokenPainel | null; erro: string | null };

export const PERIODOS = [
  { valor: "now-1h", rotulo: "1 h" }, { valor: "now-6h", rotulo: "6 h" }, { valor: "now-24h", rotulo: "24 h" },
  { valor: "now-7d", rotulo: "7 dias" }, { valor: "now-30d", rotulo: "30 dias" },
];

let atual: Estado = { dados: null, erro: null };
let pedido: Promise<void> | null = null;
let relogio: number | undefined;
const ouvintes = new Set<(e: Estado) => void>();

function publicar(e: Estado) {
  atual = e;
  ouvintes.forEach((f) => f(e));
}

function renovar(): Promise<void> {
  if (pedido) return pedido;
  pedido = api<TokenPainel>("/v1/auth/token-painel", { metodo: "POST" })
    .then((dados) => {
      publicar({ dados, erro: null });
      window.clearTimeout(relogio);
      const falta = new Date(dados.expira_em).getTime() - Date.now() - 120000;
      relogio = window.setTimeout(() => void renovar(), Math.max(30000, falta));
    })
    .catch((e) => publicar({ dados: null, erro: e instanceof ErroApi ? e.message : "falha de rede" }))
    .finally(() => { pedido = null; });
  return pedido;
}

/** Token de painel compartilhado por todos os painéis da tela, renovado sozinho. */
export function useTokenPainel(): Estado {
  const [estado, setEstado] = useState<Estado>(atual);
  useEffect(() => {
    ouvintes.add(setEstado);
    if (!atual.dados || new Date(atual.dados.expira_em).getTime() - Date.now() < 120000) void renovar();
    return () => { ouvintes.delete(setEstado); };
  }, []);
  return estado;
}

/** Endereço do painel (ou do dashboard inteiro, com `painel` ausente) para a operadora. */
export function urlPainel(t: TokenPainel, empresa: string, uid: string, opcoes: { painel?: number; de?: string; ate?: string; variaveis?: Record<string, string> } = {}): string | null {
  const org = t.organizacoes.find((o) => o.empresa === empresa)?.org_id;
  if (!org) return null;
  const q = new URLSearchParams({ orgId: String(org), from: opcoes.de ?? "now-6h", to: opcoes.ate ?? "now", theme: document.documentElement.dataset.modo === "claro" ? "light" : "dark", refresh: "1m", auth_token: t.token });
  Object.entries(opcoes.variaveis ?? {}).forEach(([k, v]) => q.set(`var-${k}`, v));
  if (opcoes.painel !== undefined) q.set("panelId", String(opcoes.painel));
  return `${t.grafana_url.replace(/\/$/, "")}/${opcoes.painel !== undefined ? "d-solo" : "d"}/${uid}/_?${q}`;
}

/** Um painel do Grafana (iframe), com título acessível e mensagem quando os painéis estão indisponíveis. */
export function PainelGrafana({ empresa, uid, painel, titulo, de, ate, variaveis, altura = 260 }: {
  empresa: string; uid: string; painel: number; titulo: string; de: string; ate?: string; variaveis?: Record<string, string>; altura?: number;
}) {
  const { dados, erro } = useTokenPainel();
  if (erro) return <p className="nx-suave painel-grafana-erro">Painéis indisponíveis: {erro}</p>;
  if (!dados) return <div className="painel-grafana carregando" style={{ height: altura }} aria-busy>carregando painel…</div>;
  const src = urlPainel(dados, empresa, uid, { painel, de, ate, variaveis });
  if (!src) return <p className="nx-suave painel-grafana-erro">Sem painéis desta operadora para o seu usuário.</p>;
  return <iframe className="painel-grafana" title={`Painel: ${titulo}`} src={src} style={{ height: altura }} loading="lazy" />;
}

/** Seletor de período dos painéis (padrão do Grafana: 1 h a 30 dias). */
export function SeletorPeriodo({ valor, aoMudar }: { valor: string; aoMudar: (v: string) => void }) {
  return (
    <span className="seletor-periodo" role="group" aria-label="Período dos painéis">
      {PERIODOS.map((p) => (
        <button key={p.valor} type="button" className={`nx-botao${valor === p.valor ? " ativo" : ""}`} aria-pressed={valor === p.valor}
          onClick={() => aoMudar(p.valor)}>{p.rotulo}</button>
      ))}
    </span>
  );
}

/** Link para abrir o dashboard inteiro no Grafana (nova aba, mesmo login). */
export function LinkGrafana({ empresa, uid, de, variaveis, children }: { empresa: string; uid: string; de: string; variaveis?: Record<string, string>; children: React.ReactNode }) {
  const { dados } = useTokenPainel();
  const href = dados ? urlPainel(dados, empresa, uid, { de, variaveis }) : null;
  return href ? <a href={href} target="_blank" rel="noopener noreferrer" className="nx-botao">{children}</a> : null;
}

/** Painel que abre em tela cheia ao clicar (Voltar e Esc fecham; o foco volta para a miniatura). */
export function PainelExpansivel(props: { empresa: string; uid: string; painel: number; titulo: string; de: string; variaveis?: Record<string, string>; altura?: number }) {
  const [cheio, setCheio] = useState(false);
  const miniatura = useRef<HTMLButtonElement>(null);
  const voltar = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!cheio) return;
    voltar.current?.focus();
    const tecla = (ev: KeyboardEvent) => { if (ev.key === "Escape") { ev.stopPropagation(); setCheio(false); } };
    window.addEventListener("keydown", tecla, true);
    return () => { window.removeEventListener("keydown", tecla, true); miniatura.current?.focus(); };
  }, [cheio]);
  return (
    <div className="painel-expansivel">
      <PainelGrafana {...props} />
      <button ref={miniatura} type="button" className="abrir-tela-cheia" aria-label={`Abrir ${props.titulo} em tela cheia`} onClick={() => setCheio(true)}>
        <span aria-hidden>⛶</span></button>
      {cheio && (
        <div className="tela-cheia-grafana" role="dialog" aria-modal="true" aria-label={props.titulo}>
          <header><button ref={voltar} type="button" className="nx-botao" onClick={() => setCheio(false)}>‹ Voltar (Esc)</button><h3>{props.titulo}</h3></header>
          <PainelGrafana {...props} altura={Math.max(360, window.innerHeight - 96)} />
        </div>
      )}
    </div>
  );
}

